<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";

import theCard from "@/components/theCard.vue";
import theProductCard from "@/components/theProductCard.vue";
import { useCardBookmark } from "@/composables/useCardBookmark";
import { useCardTelemetry } from "@/composables/useCardTelemetry";
import isUnauth from "@/utils/isUnauth";

const props = withDefaults(
  defineProps<{
    result: any;
    //eslint-disable-next-line
    index?: number;
    status?: any;
    forCarousel?: boolean;
    cardView?: string;
    cardStyle?: string;
    priceView?: string;
    topicView?: boolean;
    isStoreContent?: boolean;
    showDescription?: boolean;
  }>(),
  {
    index: 0,
    status: null,
    forCarousel: false,
    cardView: "Grid",
    cardStyle: "Grid",
    priceView: "USD",
    topicView: false,
    isStoreContent: false,
    showDescription: true,
  }
);

const router = useRouter();

// Composables
const {
  bookmarkMe,
  debouncedBookmarkMe,
  addContentToMyList,
  removeContentFromMyList,
} = useCardBookmark();

const { sendTelemetry: sendTelemetryBase } = useCardTelemetry({
  onSuccess: (resultLink: string) => {
    // For standalone labs, navigate to external URL; otherwise use router
    if (isStandaloneLab.value && props.result?.url) {
      window.open(resultLink, "_blank", "noopener,noreferrer");
    } else {
      router.push(resultLink);
    }
  },
});

// Computed properties
const isStandaloneLab = computed((): boolean => {
  return props.result?.lab_type === "standalone-labs";
});

const resultLink = computed(() => {
  // For standalone labs, use the external URL directly since there's no overview page
  if (isStandaloneLab.value && props.result?.url) {
    return props.result.url;
  }

  const labsId = props.result?.friendly_url || props.result?.id;

  return labsId
    ? router.resolve({ name: "lab", params: { labId: labsId } }).href
    : "";
});

// Methods
const sendTelemetry = async () => {
  await sendTelemetryBase(props, resultLink.value);
};

defineExpose({
  isStandaloneLab,
  resultLink,
  bookmarkMe,
  debouncedBookmarkMe,
  sendTelemetry,
  removeContentFromMyList,
  addContentToMyList,
});
</script>

<template>
  <div
    :class="[
      'relative z-10 h-full hover:z-[700] focus:z-[700] active:z-[700]',
      {
        'w-[19rem]': props.cardView === 'Grid' && !topicView,
        'w-full': props.cardView !== 'Grid' && !topicView,
        'w-[22rem]': topicView,
      },
    ]"
    data-cy="labs-result"
    v-show="result"
  >
    <the-card
      v-if="!isStoreContent"
      :is-unauth="isUnauth()"
      :for-carousel="forCarousel"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :result="result"
      :status="status"
      :cy="'labs-card'"
      :link="resultLink"
      :purchaseType="'purchase'"
      :show-description="props.showDescription"
      @navigate="sendTelemetry"
      @bookmark="debouncedBookmarkMe(result)"
    />
    <the-product-card
      v-else
      :is-unauth="isUnauth()"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :price-view="priceView"
      :result="result"
      :status="status"
      :cy="'labs-product-card'"
      :link="resultLink"
      :show-description="props.showDescription"
      @navigate="sendTelemetry"
      @bookmark="debouncedBookmarkMe(result)"
    />
  </div>
</template>
